import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  ProviderToken,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { connectState } from '@examdojo/angular/util';
import { AnimationDuration, getAnimationAppearDisappear } from '@examdojo/animation';
import { LoaderComponent } from '@examdojo/core/loader';
import { DebuggingContextService } from '@examdojo/debugging';
import { InternationalizationService } from '@examdojo/core/i18n';
import { ErrorHandlerService } from '@examdojo/error-handling';
import { FullscreenFlowService } from '@examdojo/fullscreen-flow';
import { PlatformService } from '@examdojo/platform';
import {
  PracticeActivityCandidateStoreModel,
  PracticeActivityMetadata,
  PracticeActivityQuery,
  PracticeActivityService,
  PracticeActivityStatus,
  PracticeActivityStrategy,
} from '@examdojo/practice-activity';
import {
  FetchAttemptWhenMismatchEffectService,
  provideLocalMcqSelection,
  provideSingleAttemptSelfGradingSelection,
  QUESTION_CONTEXT,
  QUESTION_PROGRESS_DATA_SOURCE,
  QuestionAttemptEntityQuery,
  QuestionAttemptEntityService,
  QuestionAttemptRealtimeService,
  QuestionAttemptStoreModel,
  QuestionFooterDesktopComponent,
  QuestionFooterMobileComponent,
  QuestionGradingRealtimeService,
  removeSolutionCaptureQueryParamWithoutReloadState,
  SingleAttemptSelfGradingSelectionService,
} from '@examdojo/question';
import { mapToVoid } from '@examdojo/rxjs';
import { SolutionCaptureQueryParam } from '@examdojo/solution';
import { assertNonNullable } from '@examdojo/util/assert';
import { isNotNullish } from '@examdojo/util/nullish';
import { IonContent } from '@ionic/angular/standalone';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  delay,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  first,
  map,
  merge,
  Observable,
  of,
  startWith,
  switchMap,
  take,
  tap,
  throwError,
  withLatestFrom,
} from 'rxjs';
import {
  ExamdojoQuestionComponent,
  PostActivityScreensService,
  QuestionAttemptFullscreenFlowService,
} from '../../../../question-v2';
import { ActivityConflictHandlerService } from '../../../../shared/activity-conflict';
import { isScopeMismatchError } from '../../../../shared/scope-mismatch';
import { CloseSolutionCaptureDialogEffectService } from '../../effects/close-solution-capture-dialog-effect.service';
import { providePracticeActivityMismatchEffect } from '../../effects/practice-activity-mismatch.adapter';
import { QuestionSelectionComponent } from '../question-selection/question-selection.component';
import { TopicPracticeQuestionProgressDataSource } from '../topic-practice-question-progress-data-source.service';
import { TopicPracticeV2Service } from '../topic-practice-v2.service';
import { provideFullscreenFlowConfig } from './provide-fullscreen-flow-config';
import { TopicPracticeDialogService } from './topic-practice-dialog.service';

@Component({
  selector: 'dojo-topic-practice-dialog-container',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './topic-practice-dialog-container.component.html',
  imports: [
    IonContent,
    QuestionSelectionComponent,
    ExamdojoQuestionComponent,
    LoaderComponent,
    QuestionFooterMobileComponent,
    QuestionFooterDesktopComponent,
  ],
  animations: [
    getAnimationAppearDisappear({
      leave: AnimationDuration.None,
    }),
  ],
  host: { class: 'flex flex-col h-full safe-area-top safe-area-bottom' },
  providers: [
    ...provideFullscreenFlowConfig(),
    PostActivityScreensService,
    QuestionGradingRealtimeService,
    QuestionAttemptRealtimeService,
    CloseSolutionCaptureDialogEffectService,
    QuestionAttemptFullscreenFlowService,
    ...providePracticeActivityMismatchEffect(),
    provideLocalMcqSelection(),
    provideSingleAttemptSelfGradingSelection(),
    {
      provide: QUESTION_CONTEXT,
      useValue: { showHints: true, optionsSelectable: true, revealSolutionOnWrongAnswer: false },
    },
    {
      provide: QUESTION_PROGRESS_DATA_SOURCE,
      useClass: TopicPracticeQuestionProgressDataSource,
    },
  ],
})
export class TopicPracticeDialogContainerComponent {
  constructor(
    private readonly route: ActivatedRoute,
    private readonly location: Location,
    private readonly topicPracticeService: TopicPracticeV2Service,
    private readonly practiceActivityService: PracticeActivityService,
    private readonly activityConflictHandler: ActivityConflictHandlerService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly internationalizationService: InternationalizationService,
    private readonly practiceActivityQuery: PracticeActivityQuery,
    private readonly destroyRef: DestroyRef,
    private readonly questionAttemptService: QuestionAttemptEntityService,
    private readonly questionAttemptQuery: QuestionAttemptEntityQuery,
    private readonly debuggingContextService: DebuggingContextService,
    private readonly topicPracticeDialogService: TopicPracticeDialogService,
    private readonly postActivityScreensService: PostActivityScreensService,
    private readonly fullscreenFlowService: FullscreenFlowService,
    private readonly platformService: PlatformService,
  ) {
    merge(
      this.autoSelectSingleCandidateOnPracticeActivityChange(),
      this.fetchPracticeActivityCandidatesOnPracticeActivityChange(),
      this.fetchQuestionAttemptOnPracticeActivityChange(),
      this.setSolutionCaptureQueryParamWithoutReload(),
      this.debuggingContextService.syncDebuggingContext('Activity id', this.inProgressPracticeActivityId$),
    )
      .pipe(takeUntilDestroyed())
      .subscribe();
  }

  private readonly instantiatedServices = [
    QuestionGradingRealtimeService,
    QuestionAttemptRealtimeService,
    CloseSolutionCaptureDialogEffectService,
    QuestionAttemptFullscreenFlowService,
    FetchAttemptWhenMismatchEffectService,
    SingleAttemptSelfGradingSelectionService,
  ].map((service: ProviderToken<unknown>) => inject(service));

  readonly questionComponent = viewChild(ExamdojoQuestionComponent);
  readonly isDesktop = this.platformService.isDesktop;
  readonly isCanvasVisible = computed(() => this.questionComponent()?.isCanvasVisible() ?? false);
  readonly processingAction = signal<'continue' | 'stop' | null>(null);

  readonly notStartedPracticeActivityId$: Observable<string | undefined> =
    this.practiceActivityQuery.notStartedPracticeActivityId$;

  readonly inProgressPracticeActivityId$: Observable<string | undefined> =
    this.practiceActivityQuery.inProgressPracticeActivityId$;

  readonly isQuestionCandidatesLoaded$$ = new BehaviorSubject<boolean>(false);
  readonly isQuestionAttemptLoaded$$ = new BehaviorSubject<boolean>(false);

  readonly candidates$ = this.topicPracticeService.candidates$;

  private readonly isPracticeActivityLoaded$ = combineLatest([
    this.questionAttemptQuery.questionLoaded$,
    this.isQuestionCandidatesLoaded$$,
    this.inProgressPracticeActivityId$,
    this.notStartedPracticeActivityId$,
    this.practiceActivityService.creatingPracticeActivity$,
  ]).pipe(
    map(
      ([
        isQuestionAttemptLoaded,
        isQuestionCandidatesLoaded,
        inProgressPracticeActivityId,
        notStartedPracticeActivityId,
        creatingPracticeActivity,
      ]) => {
        if (creatingPracticeActivity) {
          return false;
        }

        if (!inProgressPracticeActivityId && !notStartedPracticeActivityId) {
          // closing the dialog
          return true;
        }

        return (
          (isQuestionAttemptLoaded && !!inProgressPracticeActivityId) ||
          (isQuestionCandidatesLoaded && !!notStartedPracticeActivityId)
        );
      },
    ),
    distinctUntilChanged(),
    debounceTime(200),
  );

  readonly state = connectState({
    ongoingActivityId: this.practiceActivityQuery.ongoingActivityId$,
    inProgressActivityId: this.inProgressPracticeActivityId$,
    notStartedActivityId: this.notStartedPracticeActivityId$,
    candidates: this.candidates$,
    isLoading: this.isPracticeActivityLoaded$.pipe(
      map((loaded) => !loaded),
      startWith(true),
    ),
    isSmall: this.platformService.isSmall$,
    areAllQuestionsCompleted: this.topicPracticeService.areAllQuestionsCompleted$,
    isMultiQuestionActivity: this.topicPracticeService.isMultiQuestionActivity$,
    isTransitioningBetweenQuestions: this.topicPracticeService.isTransitioningBetweenQuestions$.pipe(
      switchMap((isTransitioning) => {
        if (isTransitioning) {
          return of(true);
        }

        return of(true).pipe(
          // Preventing some layout shifting
          // Maybe we can do it with just CSS
          delay(500),
          map(() => false),
        );
      }),
      startWith(false),
    ),
  });

  handleCandidateSelected(candidate: PracticeActivityCandidateStoreModel) {
    this.isQuestionCandidatesLoaded$$.next(false);

    return this.notStartedPracticeActivityId$
      .pipe(
        filter(isNotNullish),
        take(1),
        switchMap((practiceActivityId) => {
          const firstQuestionId = candidate.questionIds[0];
          assertNonNullable(firstQuestionId, 'firstQuestionId');

          return this.practiceActivityService
            .startPracticeActivity(firstQuestionId, practiceActivityId, candidate.id)
            .pipe(
              // TODO: a custom error title would be nice.
              this.errorHandlerService.catchHttpErrors(() => EMPTY),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  handleCompletedQuestion(action: 'continue' | 'stop') {
    if (this.processingAction()) {
      return;
    }

    const activityId = this.state.inProgressActivityId || this.state.notStartedActivityId;
    assertNonNullable(activityId, 'activityId');
    const activity = this.practiceActivityQuery.getEntity(activityId);
    const metadata = activity?.metadata as PracticeActivityMetadata;
    const redirectTo = metadata?.redirectTo;

    if (action === 'stop') {
      this.processingAction.set('stop');
      this.completeCurrentPracticeActivity(activityId, action, redirectTo)
        .pipe(
          take(1),
          finalize(() => this.processingAction.set(null)),
        )
        .subscribe();
      return;
    }

    this.topicPracticeService.areAllQuestionsCompleted$
      .pipe(
        take(1),
        switchMap((allCompleted) => {
          if (allCompleted) {
            this.processingAction.set('continue');
            return this.completeCurrentPracticeActivity(activityId, action, redirectTo).pipe(
              finalize(() => this.processingAction.set(null)),
            );
          }

          this.topicPracticeService.setTransitioning(true);

          return this.topicPracticeService.nextIncompleteQuestion$.pipe(
            first(isNotNullish),
            withLatestFrom(this.practiceActivityQuery.inProgressPracticeActivityId$),
            switchMap(([questionCompletionEntity, inProgressPracticeActivityId]) =>
              this.questionAttemptService
                .createAttemptForQuestion(
                  questionCompletionEntity.questionId,
                  questionCompletionEntity.lastAttempt?.id ?? null,
                )
                .pipe(map((attempt) => ({ attempt, inProgressPracticeActivityId }))),
            ),
            tap(({ attempt, inProgressPracticeActivityId }) => {
              assertNonNullable(inProgressPracticeActivityId, 'inProgressPracticeActivityId');
              // Update the practice activity store immediately to prevent mismatch detection
              this.practiceActivityService.updatePracticeActivityInStore(inProgressPracticeActivityId, {
                question_attempt_id: attempt.id,
              });
            }),
            switchMap(({ attempt, inProgressPracticeActivityId }) => {
              assertNonNullable(inProgressPracticeActivityId, 'inProgressPracticeActivityId');
              return this.practiceActivityService.fetchQuestionAttempts(attempt.id, inProgressPracticeActivityId);
            }),
            finalize(() => {
              this.topicPracticeService.setTransitioning(false);
            }),
            takeUntilDestroyed(this.destroyRef),
          );
        }),
        this.errorHandlerService.catchHttpErrors(() => this.closeDialogAndReturnHome(redirectTo)),
      )
      .subscribe();
  }

  private completeCurrentPracticeActivity(activityId: string, action: 'continue' | 'stop', redirectTo?: string) {
    // TODO: change when new statuses will be introduced
    const createNewPracticeActivity = action === 'continue' && !redirectTo ? this.getNewPracticeActivityConfig() : null;

    if (createNewPracticeActivity) {
      this.practiceActivityService.creatingPracticeActivity$$.next(true);
    }

    const practiceActivityStatusToUpdate = this.getPracticeActivityStatusBasedOnQuestionAttemptStatus(
      this.questionAttemptQuery.getActive()?.attempt?.status,
    );

    const presentPostActivityScreens$ =
      action === 'continue'
        ? this.topicPracticeService.isMultiQuestionActivity$.pipe(
            first(),
            switchMap((isMultiQuestionActivity) => {
              if (!isMultiQuestionActivity) {
                return of(null);
              }
              return this.postActivityScreensService.postActivityScreens$.pipe(
                first(),
                switchMap((steps) => this.fullscreenFlowService.open(steps, 1)),
              );
            }),
          )
        : of(null);

    return this.practiceActivityService
      .completePracticeActivityWithStatus(activityId, practiceActivityStatusToUpdate)
      .pipe(
        switchMap(() => presentPostActivityScreens$),
        tap(() => {
          this.practiceActivityService.resetStore();
          this.questionAttemptService.resetStore();
        }),
        switchMap(() => {
          if (!createNewPracticeActivity) {
            return this.closeDialogAndReturnHome(redirectTo);
          }

          return this.practiceActivityService
            .create({
              topics_level_02: createNewPracticeActivity.topicIds,
              strategy: createNewPracticeActivity.strategy,
            })
            .pipe(
              this.activityConflictHandler.handleConflict({ targetActivity: 'practice' }),
              this.errorHandlerService.catchHttpErrors(
                (err) => (isScopeMismatchError(err) ? throwError(() => err) : EMPTY),
                { ignoreReporting: isScopeMismatchError },
              ),
              this.errorHandlerService.catchError('Activity scope mismatch', () => EMPTY, {
                toast: this.internationalizationService.translate('examdojo.http_errors.scope_mismatch'),
              }),
              finalize(() => {
                this.practiceActivityService.creatingPracticeActivity$$.next(false);
              }),
            );
        }),
        // If the practice activity creation fails, we should dismiss the dialog and navigate to the home page.
        // Otherwise the user will be stuck in the dialog forever.
        this.errorHandlerService.catchHttpErrors(() => this.closeDialogAndReturnHome(redirectTo)),
        // Do not use takeUntilDestroyed here
        // as after closing the dialog we still want to finish the flow.
      );
  }

  private getNewPracticeActivityConfig() {
    const lastSelection = this.topicPracticeService.topicLevel2SelectionStorageManager.getValue();
    const topicIds = lastSelection?.topicIds ?? [];
    const strategy = lastSelection?.strategy ?? PracticeActivityStrategy.ScoreImpact;

    if (!topicIds?.length) {
      return null;
    }

    return { topicIds, strategy };
  }

  private closeDialogAndReturnHome(redirectTo?: string) {
    return this.topicPracticeDialogService.dismissPracticeActivityDialog(redirectTo);
  }

  private autoSelectSingleCandidateOnPracticeActivityChange() {
    return this.notStartedPracticeActivityId$.pipe(
      filter(isNotNullish),
      tap(() => this.practiceActivityService.setCandidates(null)),
      switchMap(() => this.candidates$.pipe(first(isNotNullish))),
      this.errorHandlerService.catchHttpErrors(() => EMPTY),
      tap((candidates) => {
        if (candidates.length === 1) {
          this.handleCandidateSelected(candidates[0]);
        } else {
          this.isQuestionCandidatesLoaded$$.next(true);
        }
      }),
    );
  }

  private fetchPracticeActivityCandidatesOnPracticeActivityChange() {
    return this.practiceActivityQuery.ongoingActivity$.pipe(
      map((activity) => activity?.id),
      filter(isNotNullish),
      distinctUntilChanged(),
      switchMap((id) => this.practiceActivityService.fetchPracticeActivityCandidates(id)),
      this.errorHandlerService.catchHttpErrors(() => EMPTY),
    );
  }

  private fetchQuestionAttemptOnPracticeActivityChange() {
    return this.inProgressPracticeActivityId$.pipe(
      filter(isNotNullish),
      distinctUntilChanged(),
      tap(() => {
        // reset store immediately to prevent showing stale active question when returning to dialog
        this.questionAttemptService.resetStore();
      }),
      switchMap((activityId) =>
        this.practiceActivityQuery.selectEntity(activityId).pipe(
          map((activity) => activity?.question_attempt_id),
          first(isNotNullish),
          switchMap((attemptId) =>
            this.practiceActivityService.fetchQuestionAttempts(attemptId, activityId, { resetStore: false }),
          ),
        ),
      ),
      mapToVoid(),
    );
  }

  private setSolutionCaptureQueryParamWithoutReload() {
    return this.route.queryParamMap.pipe(
      switchMap((params) =>
        this.questionAttemptQuery.active$.pipe(
          tap(() => {
            if (params.get(SolutionCaptureQueryParam)) {
              this.questionAttemptService.setOpenCaptureDialogFlag(true);
              this.location.replaceState(removeSolutionCaptureQueryParamWithoutReloadState());
            }
          }),
        ),
      ),
    );
  }

  private getPracticeActivityStatusBasedOnQuestionAttemptStatus(
    questionAttemtStatus: QuestionAttemptStoreModel['status'] | undefined,
  ): PracticeActivityStatus {
    return !questionAttemtStatus || questionAttemtStatus === 'PENDING' ? 'DISCARDED' : 'COMPLETED';
  }
}
